import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { NgRedux } from '@angular-redux/store';
import { IAppState, Actions, AuthResponse, CustomerInfo } from '@core/redux/stores';

import {
  ModalService,
  ConfirmModalService,
  AuthTrackingService,
  ProxyService,
  ValidationService,
  AlertService,
  AuthenticationService,
  AppleRedirectResult,
  ApiService,
  AsyncValidatorWExt,
  CustomizeHttpheaderService,
  GtmService,
  ResolutionService,
  AccessibilityService,
  UtilitiesService,
  ScriptLoaderService,
} from '@core/services';

import { Subscription } from 'rxjs';

import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { RequestUser, LoginIntent } from '@features/authenticate/models';

import { ErrorCodes } from '@shared/models/error-codes/response.error-codes';
import { LoggingService } from '@core/services/logging.service';
import { GtmExternal, ModalTrackingGtmDto } from '@shared/models/configs/gtm.external';
import { environment } from '@environments/environment';
import { Constants } from '@app/core/constants/core.constants';
import { SurveyService } from '@core/services/survey.service';

// declare var window: any;
// declare var FB: any;
declare var gapi: any;

@Component({
  moduleId: module.id,
  selector: 'app-signin-body',
  templateUrl: './signin.component.html',
  styleUrls: ['../register/register.component.less'],
  providers: [AlertService]
})

export class SigninModalComponent implements OnInit, AfterViewInit, OnDestroy {

  private returnUrl: string;
  private subscriptions = new Subscription();

  public signInForm: UntypedFormGroup;
  public disableSignInButton: boolean;
  public loading: boolean;
  public paramObj: any;
  public appleId: string;


  constructor(
    public alertService: AlertService,
    private ngRedux: NgRedux<IAppState>,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private confirmModalService: ConfirmModalService,
    private authTracking: AuthTrackingService,
    private proxyService: ProxyService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private config: ApiService,
    private registrationService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private logger: LoggingService,
    private customizeHeaderService: CustomizeHttpheaderService,
    private gtmService: GtmService,
    private resolution: ResolutionService,
    private accessibilityService: AccessibilityService,
    private survey: SurveyService) {
    this.returnUrl = '';
    this.loading = false;
    this.disableSignInButton = true;
    this.registrationService.setAlertService(this.alertService);

    this.subscriptions.add(
      this.confirmModalService.btnEvent.subscribe(
        async (data: string) => {
          this.disableSignInButton = false;
          if (data === 'resetPWBtn') {
            // user wants to go to the forgot password modal if they clicked btn1 on the confirm modal.
            this.showModal('app-forgot-password');
          } else if (data === 'sendCode') {
            const gtmModalTracking = new ModalTrackingGtmDto();
            gtmModalTracking.pageName = GtmExternal.VALUE_SIGNIN;
            gtmModalTracking.clickText = GtmExternal.VALUE_CLICK_TEXT_SEND_CODE;
            gtmModalTracking.event = GtmExternal.EVENT_CTA_CLICK;
            this.gtmService.sendModalEvent(gtmModalTracking);
            await this.registrationService.requestMFACode(false);
          } else {
            // close was pressed, need further action is needed
          }
          this.loading = false;
        }
      )
    );
  }

  ngOnInit() {
    this.createForm();
    this.subscriptions.add(
      this.resolution.width.subscribe(
        (width) => {
          if (width > environment.MAX_MOBILE_WIDTH) {
            this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.DISPLAYINLINEBLOCKIMPORTANT);
          } else {
            this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.SIGNINSIGNUPMODALPOSITION);

          }
        }
      )
    );
  }

  async ngAfterViewInit() {
    this.gtmService.sendPageName(GtmExternal.VALUE_SIGNIN);
    // const showTerms = true;
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe(
        (params: Params) => {
          this.returnUrl = params[Constants.RETURN_URL] as string;
          if (this.returnUrl) {
            this.returnUrl = decodeURIComponent(this.returnUrl);
          }
        }
      )
    );
    const snapshotParams = this.activatedRoute.snapshot.queryParams;
    if (snapshotParams[Constants.APPLE_SIGNIN_ORIGIN]) {
      const appleResult = await this.registrationService.handleAppleSignInRedirect(snapshotParams);
      if (appleResult.status === 'success' && this.registrationService.getErrorMessage() === null) {
        this.onAuthSuccess();
      } else if (appleResult.status !== 'terms_pending'
        && appleResult.status !== 'incomplete'
        && this.registrationService.getErrorMessage() !== null) {
        this.alertService.error(this.registrationService.getErrorMessage());
      }
      this.loading = false;
      this.disableSignInButton = false;
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    if (!this.authTracking.isLoggedIn() && this.returnUrl) {
      const urlArray = this.router.url.split('?');
      this.router.navigate([urlArray[0]]);
    }
    const params = this.activatedRoute.snapshot.queryParams;
    if (params[Constants.APPLE_SIGNIN_ORIGIN] && ((params[Constants.CANCELLED] || params[Constants.SUCCESS] === 'false') || this.authTracking.isLoggedIn())) {
      this.registrationService.cleanUpAppleSigninQueryParams();
    }
    this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.DISPLAYINLINEBLOCKIMPORTANT);
  }

  createForm() {
    this.signInForm = this.fb.group({
      username: new UntypedFormControl(null, [Validators.required], [AsyncValidatorWExt.debounce(ValidationService.asyncEmailValidator, 800)]),
      password: new UntypedFormControl(null, [Validators.required]),
    });

    // this enables the button to send for submit when the values are changed and the form values
    // are all valid
    this.subscriptions.add(
      this.signInForm.valueChanges.subscribe(
        () => {
          this.checkForm();
        }
      )
    );
  }

  checkForm() {
    if (this.signInForm.valid) {
      this.disableSignInButton = false;
    } else {
      if (this.signInForm.controls.username.value && this.signInForm.controls.password.value) {
        this.disableSignInButton = false;
      } else {
        this.disableSignInButton = true;
      }
    }
  }

  showSignupModal(): void {
    this.showModal(this.modalService.getSignupModalSelector(), false);
  }

  showModal(name: string, autoHide: boolean = true) {
      setTimeout(
        () => {
          if (this.returnUrl) {
            this.router.navigate(['/home'], { queryParams:
              UtilitiesService.generateQueryParams([Constants.RETURN_URL], [this.returnUrl])});
          }
        },
        1500
      );

    setTimeout(() => {
      this.modalService.setModalSelector(name);
      this.modalService.showModal(autoHide);
      this.logger.log(name);
    }, 200);
  }

  onSubmit(validForm: boolean) {

    const gtmModalTracking = new ModalTrackingGtmDto();
    gtmModalTracking.pageName = GtmExternal.VALUE_SIGNIN;
    gtmModalTracking.clickText = GtmExternal.VALUE_CLICK_TEXT_SIGN_IN;
    gtmModalTracking.event = GtmExternal.EVENT_CTA_CLICK;
    this.gtmService.sendModalEvent(gtmModalTracking);

    if (validForm) {
      this.loading = true;
      this.disableSignInButton = true;
      const formModel = this.signInForm.value;
      const userData: RequestUser = {
        email: formModel.username as string,
        password: formModel.password as string,
        deviceDetails: null,
        intent: LoginIntent.Login
      };

      this.signInLocalUser(userData);
    }
  }

  public signInLocalUser(userData: RequestUser) {
    this.subscriptions.add(
      this.proxyService.httpPost(ApiRoutes.LOGIN, userData,
         this.customizeHeaderService.addLongAndLat()).subscribe(
        (response) => {
          const gtmModalTracking = new ModalTrackingGtmDto();
          gtmModalTracking.pageName = GtmExternal.VALUE_SIGNIN;
          gtmModalTracking.modal = GtmExternal.VALUE_MODAL_SUCCESS;
          gtmModalTracking.event = GtmExternal.EVENT_SIGNIN_SUCCESS;
          this.gtmService.sendModalEvent(gtmModalTracking);
          const signInResponse = <AuthResponse>response.body.valueOf();
          this.registrationService.setUserData(signInResponse);
          this.registrationService.sendGTMSignInEvent(signInResponse.user, false);
          this.loading = false;
          this.disableSignInButton = false;
          this.onAuthSuccess();
        },
        (err) => {
          this.processErr(err, userData.email);
        }
      )
    );
  }

  async facebookAuth() {
    this.loading = true;
    this.disableSignInButton = true;
    this.alertService.clear();
    this.cdr.detectChanges();
    await this.registrationService.facebookRegister(false, true);
    if (this.registrationService.facebookProfile) {
      if (this.registrationService.doesAccountPreexist) {
        await this.registrationService.completeRegistration();
        if (this.registrationService.getErrorMessage() === null) {
          this.onAuthSuccess();
        } else {
          this.alertService.error(this.registrationService.getErrorMessage());
        }
      }
    }
    this.loading = false;
    this.disableSignInButton = false;
  }

  async googleAuth() {
    this.loading = true;
    this.disableSignInButton = true;
    this.alertService.clear();
    this.cdr.detectChanges();
    await this.registrationService.googleRegister(false, true);
    // await this.registrationService.completeRegistration();
    if (this.registrationService.getErrorMessage() === null) {
      this.onAuthSuccess();
    } else {
      this.alertService.error(this.registrationService.getErrorMessage());
    }
    this.loading = false;
    this.disableSignInButton = false;
  }

  async appleAuth() {
    this.registrationService.appleAuth('signIn');
  }

  // process error function called when signing into whataburger back end and an error happens.
  // sign in can be done through google auth token, fb auth token, or whataburger account name/pw
  processErr(errorResponse: any, email?: string) {
    const gtmModalTracking = new ModalTrackingGtmDto();
    gtmModalTracking.pageName = GtmExternal.VALUE_SIGNIN;
    gtmModalTracking.modal = GtmExternal.VALUE_MODAL_UNSUCCESS;
    gtmModalTracking.event = GtmExternal.EVENT_ERROR;
    if (errorResponse.error.code === ErrorCodes.INCORRECT_USERNAME_OR_PWD) {
      this.alertService.error('Uh-oh, your login didn\'t work that time. Please check the email and password you entered.');
      this.loading = false;
      this.disableSignInButton = false;
    } else if (errorResponse.error.code === ErrorCodes.ACCOUNT_LCK) {
      gtmModalTracking.modal = GtmExternal.VALUE_MODAL_ACC_LOCK;
      this.confirmModalService.show('Account is now locked',
        '',
        'Due to 3 unsuccessful sign in attempts, this account is locked for 30 minutes. '
        + 'You may reset your password now or try again later.',
        'Reset Password',
        '',
        'Try Again Later',
        true,
        'resetPWBtn');
    } else if (errorResponse.error.token) {
      gtmModalTracking.modal = GtmExternal.VALUE_MODAL_NEWDEVICE_POPUP;
      this.registrationService.setMFAToken(errorResponse.error);
      this.registrationService.showNewDevicePopup(email);
    } else if (errorResponse.error.code === 4037) {
      this.loading = true;
      this.registrationService.showOKModal(errorResponse.error.code);
      this.loading = false;
    } else {
      this.alertService.error('Ooops, we\'re having a technical issue with our system. Please try again later.');
      this.loading = false;
      this.disableSignInButton = false;
    }
    if (this.config.getVerboseTracing()) {
      this.logger.log('on failure ' + JSON.stringify(errorResponse));
    }
    this.gtmService.sendModalEvent(gtmModalTracking);
  }

  getUrlParams () {
    const firstParam = this.returnUrl.split(`?`);
    let chkSecondParam;
    let hashes;
    let hashValue;
    if (firstParam[1]) {
      chkSecondParam = firstParam[1].split('&');
      hashes = firstParam[1];
      hashValue = firstParam[1].split('#');
    }
    if (hashes) {
      let keyValue;
      if (chkSecondParam.length > 0 ) {
         keyValue = chkSecondParam[0].split(`=`);
      } else {
         keyValue = hashes.split(`=`);
      }
      return {
        firstParam: firstParam[0],
        key: keyValue[0],
        val: decodeURIComponent( keyValue[1]),
        fragmentValue: hashValue[1]
      };
    } else {
      return {
        firstParam: firstParam[0],
        key: null,
        val: null,
        fragmentValue: null
      };
    }
  }

  onAuthSuccess() {
    this.survey.isQualtricsLoaded = false;
    this.modalService.hideModal();
    this.ngRedux.dispatch({ type: Actions.SUBMITTEDORDER });
    if(this.authTracking.isLoggedIn()) {
      this.subscriptions.add(
        this.authTracking.user.subscribe(
          (user) => {
            if(user) {
              if(typeof window.QSI === 'undefined') {
                window.QSI = {};
              }
              window.QSI.config = {
                externalReference: user?.id
              };
              if(!this.survey.isQualtricsLoaded) {
                // this.sur.removeScript('qualtrics-subscript');
                // this.scriptLoader.removeScript('qualtrics-wb');
                this.survey.loadScript().subscribe(
                  () => {
                    this.survey.isQualtricsLoaded = true;
                  }
                );
              }
            }
          })
      );

    }
    if(this.router.url.indexOf(this.authTracking.DELETE_ACCOUNT_ROUTE) !== -1) {
      this.authTracking.clearSignedOutUserEmail();
      this.router.navigateByUrl('/home');
    }
    if (this.returnUrl !== undefined) {
      // TODO: this could have consequences - test this thoroughly
      this.router.navigateByUrl(this.returnUrl);
    }
  }
  // loadQualtrics() {
  //   return this.scriptLoader.load({id: 'qualtrics-wb', src: '/assets/js/qualtrics.js', loaded: false});
  // }

  parseQueryString(queryString: string): Map<string, string> {
    // if the query string is NULL
    if (queryString == null) {
      queryString = window.location.search.substring(1);
    }

    const params = new Map<string, string>();

    const queries = queryString.split('&');

    queries.forEach((indexQuery: string) => {
      const indexPair = indexQuery.split('=');

      const queryKey = decodeURIComponent(indexPair[0]);
      const queryValue = decodeURIComponent(indexPair.length > 1 ? indexPair[1] : '');

      params[queryKey] = queryValue;
    });

    return params;
  }

  onEmailChange(value) {
    if (value) {
      sessionStorage.setItem('email', value);
    }
  }
}
